import { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import type { ManualIndicator } from '@/app/lib/quant/quantApi'
import { quantApi } from '@/app/lib/quant/quantApi'
import { viewFailureMessage } from '@/app/lib/quant/quantErrors'
import type { CandlePoint, MarketIndicatorSpec } from '@/app/lib/quant/types'
import {
  DEFAULT_VIEW_BARS,
  isViewErrorCode,
  NON_CLOSING_ERROR_CODES,
  RECONNECT_ERROR_CODES,
  SUPPORTED_CATALOG_VERSION,
  type MarketSession,
  type OpenedFrame,
  type ProvisionalEntry,
  type ViewFailure,
  type ViewOverlay,
  type ViewRate,
  type ViewServerFrame,
  type ViewState,
} from '@/app/lib/quant/viewProtocol'

export type QuantMarketStatus =
  | 'idle'
  | 'connecting'
  | 'warming_up'
  | 'streaming'
  | 'live'
  | 'closed'
  | 'no_feed'
  | 'reconnecting'
  | 'failed'

export interface MarketIndicatorState {
  rev: number
  last: number | null
  overlay: ViewOverlay
}

export interface QuantMarketState {
  status: QuantMarketStatus
  /** The feed state of the last `status` frame (`null` before the first one). */
  feedState: ViewState | null
  /** Free text of the last `status` frame (e.g. `warming up`, `forming stale`). */
  detail: string | null
  candles: CandlePoint[]
  /** The bar window size, from `opened.bars` (never `candles.total`, which is the chunk count). */
  barCap: number
  /** Candle chunk frames received this session (a toggle/save must not change it). */
  chunks: number
  /** Latest indicator frame per indicator id (the strategy view uses the id `strategy`). */
  indicators: Record<string, MarketIndicatorState>
  /** Line values at the forming bar; applies to the forming bar only and is empty after a close. */
  provisional: Record<string, ProvisionalEntry>
  /** True while the last candle is a forming (not yet closed) bar. */
  forming: boolean
  opened: OpenedFrame | null
  /** `opened.catalog_version` differs from the one this client was written against. */
  catalogChanged: boolean
  /** Latest pong round trip, or `null` before the first one. */
  latencyMs: number | null
  error: string | null
  errorCode: string | null
  /** True when the failure is one where a single user-initiated reopen is offered. */
  canReconnect: boolean
  /** A refused later `indicators` list (the view stays open and the old list stays in force). */
  indicatorRefusal: string | null
}

export const INITIAL_MARKET_STATE: QuantMarketState = {
  status: 'connecting',
  feedState: null,
  detail: null,
  candles: [],
  barCap: DEFAULT_VIEW_BARS,
  chunks: 0,
  indicators: {},
  provisional: {},
  forming: false,
  opened: null,
  catalogChanged: false,
  latencyMs: null,
  error: null,
  errorCode: null,
  canReconnect: false,
  indicatorRefusal: null,
}

export interface KeyedMarketState extends QuantMarketState {
  key: string
}

export type MarketAction = { key: string; frame: ViewServerFrame } | { key: string; failure: ViewFailure }

/** The hook status for a feed `status.state`; non-`live` states keep candles and overlays. */
export function statusForFeedState(state: ViewState): QuantMarketStatus {
  switch (state) {
    case 'opening':
      return 'warming_up'
    case 'live':
      return 'live'
    case 'closed':
      return 'closed'
    case 'no_feed':
    case 'redis_down':
      return 'no_feed'
    case 'replaying':
    case 'reconnecting':
    case 'closing':
      return 'reconnecting'
  }
}

/**
 * Applies a `tick`. Driven only by frames: no clock decides a candle boundary. `isNew` appends the
 * forming bar, otherwise the bar with the same (bucket-close) `time` is replaced; a `closed` tick is
 * the final bar for its bucket, and the tick after it is `isNew`.
 */
function applyTick(candles: CandlePoint[], frame: Extract<ViewServerFrame, { type: 'tick' }>, cap: number): CandlePoint[] {
  const last = candles[candles.length - 1]
  const same = last !== undefined && last.time === frame.candle.time
  let next: CandlePoint[]
  if (same) next = candles.slice(0, -1).concat(frame.candle)
  else if (last === undefined || frame.candle.time > last.time) next = candles.concat(frame.candle)
  else return candles
  return next.length > cap ? next.slice(next.length - cap) : next
}

/** Pure frame reducer. An action for a different session key starts from the initial state. */
export function marketReducer(prev: KeyedMarketState, action: MarketAction): KeyedMarketState {
  const state: KeyedMarketState = prev.key === action.key ? prev : { ...INITIAL_MARKET_STATE, key: action.key }
  if ('failure' in action) {
    const f = action.failure
    if (f.closeCode === null) {
      return f.code === 'unauthorized' ? failedState(state, f) : { ...state, indicatorRefusal: viewFailureMessage(f) }
    }
    return failedState(state, f)
  }
  const frame = action.frame
  switch (frame.type) {
    case 'auth.result':
      return state
    case 'opened':
      return {
        ...state,
        opened: frame,
        barCap: frame.bars,
        catalogChanged: frame.catalog_version !== SUPPORTED_CATALOG_VERSION,
        status: state.status === 'connecting' ? 'warming_up' : state.status,
      }
    case 'status': {
      if (state.status === 'failed') return state
      const status = statusForFeedState(frame.state)
      return { ...state, status, feedState: frame.state, detail: frame.detail ?? null }
    }
    case 'candles': {
      const candles = state.candles.concat(frame.candles)
      const status = state.status === 'connecting' || state.status === 'warming_up' ? 'streaming' : state.status
      return { ...state, candles, chunks: state.chunks + 1, status }
    }
    case 'indicator': {
      if (frame.removed === true) {
        if (!(frame.id in state.indicators)) return state
        const rest = Object.fromEntries(Object.entries(state.indicators).filter(([id]) => id !== frame.id))
        return { ...state, indicators: rest }
      }
      if (frame.overlay === undefined || frame.overlay === null) return state
      const have = state.indicators[frame.id]
      if (have !== undefined && frame.rev < have.rev) return state
      return {
        ...state,
        indicators: { ...state.indicators, [frame.id]: { rev: frame.rev, last: frame.last ?? null, overlay: frame.overlay } },
      }
    }
    case 'tick': {
      if (state.candles.length === 0) return state
      const closed = frame.closed === true
      const provisional = closed ? {} : frame.isNew ? (frame.provisional ?? {}) : (frame.provisional ?? state.provisional)
      return {
        ...state,
        candles: applyTick(state.candles, frame, state.barCap),
        provisional,
        forming: !closed,
      }
    }
    case 'pong':
      return { ...state, latencyMs: frame.latencyMs }
    case 'error': {
      // Always the coded copy, never the server text. A closing error ends the open/warm-up state.
      if (!isViewErrorCode(frame.code)) return { ...state, error: frame.message, errorCode: frame.code }
      const code = frame.code
      const message = viewFailureMessage({ code, message: frame.message, frame })
      if (state.status === 'failed' || (state.opened !== null && NON_CLOSING_ERROR_CODES.includes(code))) {
        return { ...state, error: state.status === 'failed' ? state.error ?? message : message, errorCode: code }
      }
      return failedState(state, { code, message: frame.message, closeCode: 0, frame })
    }
  }
}

function failedState(state: KeyedMarketState, f: ViewFailure): KeyedMarketState {
  return {
    ...state,
    status: 'failed',
    error: viewFailureMessage(f),
    errorCode: f.code,
    canReconnect: f.code === 'connection_lost' || (RECONNECT_ERROR_CODES as readonly string[]).includes(f.code),
  }
}

export function toSpec(i: ManualIndicator): MarketIndicatorSpec {
  return { id: i.id, type: i.type, params: i.params, color: i.color }
}

export interface UseQuantMarketArgs {
  /** The instrument (e.g. `NQ`), or `null` for no view. */
  symbol: string | null
  tf: string
  /** The indicators switched on. A change sends a list over the open socket; nothing restreams. */
  active: ManualIndicator[]
  /** 1 s or 5 s ticks. A change sends `rate`; it never reconnects. Default 1. */
  rate?: ViewRate
  /** Called once when the view first turns live after opening. */
  onRecomputed?: (info: { symbol: string; tf: string; active: number }) => void
}

export interface UseQuantMarket extends QuantMarketState {
  /** One user-initiated reopen of the same view. */
  reconnect: () => void
  /** Stops waiting for the open (closes the session, no error). `reconnect` opens it again. */
  cancel: () => void
  /** True after `cancel` until the next `reconnect` or a symbol/timeframe change. */
  cancelled: boolean
}

/**
 * Drives one live market view (spec 070). Frames only; the session behind
 * `quantApi.openMarketStream` is the seam. A symbol or timeframe change closes the session and opens
 * a new one (the candles restream); an indicator list change calls `sendIndicators`, a rate change
 * `setRate`. Unmount closes the session. No timeout anywhere: an open can take minutes.
 */
export function useQuantMarket({ symbol, tf, active, rate = 1, onRecomputed }: UseQuantMarketArgs): UseQuantMarket {
  const [attempt, setAttempt] = useState(0)
  const key = `${symbol ?? ''}|${tf}|${attempt}`
  const [cancelledKey, setCancelledKey] = useState<string | null>(null)
  const cancelled = cancelledKey === key
  const [state, dispatch] = useReducer(marketReducer, { ...INITIAL_MARKET_STATE, key })
  const sessionRef = useRef<MarketSession | null>(null)
  const activeRef = useRef(active)
  const rateRef = useRef(rate)
  const sentRef = useRef('')
  const sentRateRef = useRef<ViewRate>(rate)
  const recomputedRef = useRef(onRecomputed)
  const specKey = JSON.stringify(active.map(toSpec))

  useEffect(() => {
    activeRef.current = active
    rateRef.current = rate
    recomputedRef.current = onRecomputed
  })

  useEffect(() => {
    if (symbol === null || cancelled) return
    const specs = activeRef.current.map(toSpec)
    const startRate = rateRef.current
    const session = quantApi.openMarketStream({ instrument: symbol, tf, bars: DEFAULT_VIEW_BARS, rate_s: startRate, indicators: specs })
    sessionRef.current = session
    sentRef.current = JSON.stringify(specs)
    sentRateRef.current = startRate
    const actionKey = `${symbol}|${tf}|${attempt}`
    let wasLive = false
    const offFrame = session.onFrame((frame) => {
      if (frame.type === 'status' && frame.state === 'live' && !wasLive) {
        wasLive = true
        recomputedRef.current?.({ symbol, tf, active: activeRef.current.length })
      }
      dispatch({ key: actionKey, frame })
    })
    const offFailure = session.onFailure((failure) => dispatch({ key: actionKey, failure }))
    return () => {
      offFrame()
      offFailure()
      session.close()
      if (sessionRef.current === session) sessionRef.current = null
    }
  }, [symbol, tf, attempt, cancelled])

  useEffect(() => {
    if (specKey === sentRef.current) return
    const session = sessionRef.current
    if (session === null) return
    sentRef.current = specKey
    session.sendIndicators(activeRef.current.map(toSpec))
  }, [specKey])

  useEffect(() => {
    if (rate === sentRateRef.current) return
    const session = sessionRef.current
    if (session === null) return
    sentRateRef.current = rate
    session.setRate(rate)
  }, [rate])

  const reconnect = useCallback(() => setAttempt((n) => n + 1), [])
  const cancel = useCallback(() => setCancelledKey(key), [key])

  // A session for another key has not produced a frame yet: show the initial state.
  const view = state.key === key ? state : { ...INITIAL_MARKET_STATE, key }
  return { ...view, status: symbol === null || cancelled ? 'idle' : view.status, reconnect, cancel, cancelled }
}
